import dayjs from "dayjs";
import { LDS_AUTOCOMPLETE_MORE } from "../assets/Data/LdsAirportsAutocomplete";

/**
 * Fallback date links for "all destinations" when getLdsDates returns no data (city empty).
 * One departure date per day for the next 12 months + several return options so the calendar is fully clickable.
 */
export function getFallbackLdsDatesForAllDestinations() {
  const links = [];
  const today = dayjs().add(1, "day").startOf("day");
  const end = today.add(12, "month");
  let d = today;
  while (d.isBefore(end) || d.isSame(end, "day")) {
    for (const nights of [2, 3, 4, 5, 6, 7, 8, 10, 14]) {
      const ret = d.add(nights, "day");
      links.push({
        depDate: d.format("DD/MM/YYYY"),
        retDate: ret.format("DD/MM/YYYY"),
      });
    }
    d = d.add(1, "day");
  }
  return links;
}

function normalizeDateToDdMmYyyy(rawDate) {
  if (!rawDate) return "";
  const s = String(rawDate).trim();
  if (!s) return "";
  const slash = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    const dd = String(parseInt(slash[1], 10)).padStart(2, "0");
    const mm = String(parseInt(slash[2], 10)).padStart(2, "0");
    const yyyy = String(parseInt(slash[3], 10));
    return `${dd}/${mm}/${yyyy}`;
  }
  const dash = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (dash) {
    const yyyy = String(parseInt(dash[1], 10));
    const mm = String(parseInt(dash[2], 10)).padStart(2, "0");
    const dd = String(parseInt(dash[3], 10)).padStart(2, "0");
    return `${dd}/${mm}/${yyyy}`;
  }
  const compact = s.match(/^(\d{4})(\d{2})(\d{2})$/);
  if (compact) {
    const yyyy = compact[1];
    const mm = compact[2];
    const dd = compact[3];
    return `${dd}/${mm}/${yyyy}`;
  }
  return "";
}

function extractDepDate(link) {
  return (
    link?.depDate ??
    link?.dep_date ??
    link?.departureDate ??
    link?.departure_date ??
    ""
  );
}

function extractRetDate(link) {
  return (
    link?.retDate ??
    link?.ret_date ??
    link?.returnDate ??
    link?.return_date ??
    ""
  );
}

export function getAllDepartureDates(ldsDates) {
  if (!ldsDates) {
    return [];
  }
  const ldsDatesArr = Array.isArray(ldsDates) ? ldsDates : [ldsDates];
  const departureDates = new Set();
  ldsDatesArr.forEach((link) => {
    if (!link) return;
    const dep = normalizeDateToDdMmYyyy(extractDepDate(link));
    if (dep) departureDates.add(dep);
  });
  return Array.from(departureDates).sort();
}

function toYmdFromDdMmYyyy(s) {
  const normalized = normalizeDateToDdMmYyyy(s);
  if (!normalized) return "";
  const parts = normalized.split("/");
  if (parts.length !== 3) return "";
  const [d, m, y] = parts;
  const dayNum = parseInt(d, 10);
  const monthNum = parseInt(m, 10);
  const yearNum = parseInt(y, 10);
  if (!Number.isFinite(dayNum) || !Number.isFinite(monthNum) || !Number.isFinite(yearNum)) {
    return "";
  }
  const day = String(dayNum).padStart(2, "0");
  const month = String(monthNum).padStart(2, "0");
  const year = String(yearNum);
  return `${year}-${month}-${day}`;
}

export function getReturnDatesForDeparture(ldsDates, selectedDepDate) {
  if (Array.isArray(ldsDates)) ldsDates = ldsDates.filter((valid) => !!valid);
  if (!ldsDates || !selectedDepDate) {
    return [];
  }

  const normalizedSelected = toYmdFromDdMmYyyy(selectedDepDate);
  const ldsDatesArr = Array.isArray(ldsDates) ? ldsDates : [ldsDates];
  const returnDates = ldsDatesArr
    .filter(
      (link) =>
        link &&
        toYmdFromDdMmYyyy(extractDepDate(link)) === normalizedSelected
    )
    .map((link) => normalizeDateToDdMmYyyy(extractRetDate(link)))
    .filter((d) => typeof d === "string" && d.trim() !== "");
  if (returnDates.length > 0) return returnDates.sort();
  return getAllDepartureDates(ldsDatesArr);
}

export function getAvailableMonthOfDeparture(ldsDates) {
  if (!ldsDates) {
    return [];
  }

  const ldsDatesArr = Array.isArray(ldsDates) ? ldsDates : [ldsDates];
  const withDep = ldsDatesArr
    .map((link) => {
      if (!link || typeof link !== "object") return null;
      const dep = normalizeDateToDdMmYyyy(extractDepDate(link));
      if (!dep) return null;
      const segments = dep.split("/");
      if (segments.length !== 3) return null;
      return { dep };
    })
    .filter(Boolean);

  if (withDep.length === 0) {
    return [];
  }

  const availableMonths = new Set();

  withDep
    .sort((a, b) =>
      dayjs(a.dep, "DD/MM/YYYY").diff(dayjs(b.dep, "DD/MM/YYYY")),
    )
    .forEach(({ dep }) => {
      const [, mm, yyyy] = dep.split("/");
      if (mm && yyyy) availableMonths.add(`${mm}/${yyyy}`);
    });

  return Array.from(availableMonths);
}

/** Primary LDS destination row from SearchContext `location` (object or single-element array). */
export function getOrganizedTourPrimaryLocation(loc) {
  if (Array.isArray(loc)) {
    return loc.length > 0 ? loc[0] : null;
  }
  if (loc && typeof loc === "object") return loc;
  if (typeof loc === "string" && loc.trim()) {
    return { CITYCODE: loc.trim(), AIRPORTCODE: loc.trim() };
  }
  return null;
}

/** LDS city/airport code for /searchLds and getLdsDates — "" means all destinations. */
export function normalizeLdsCityCodeForPayload(value) {
  const code = String(value || "").trim().toUpperCase();
  if (!code || code === "ALL") return "";
  return code;
}

/**
 * Public and agent /searchLds use the same city + categories.
 * Blanking city on the public site made LDS return a truncated subset
 * (dozens of packages instead of the selected destination's inventory).
 * "ALL destinations" is already city "".
 */
export function resolveLdsSearchPayloadFilters({ cityValue, categories } = {}) {
  return {
    city: cityValue || "",
    categories: Array.isArray(categories) && categories.includes("ALL") ? [] : categories,
  };
}

function isLdsAllSentinelCategory(value) {
  return String(value || "").trim().toUpperCase() === "ALL";
}

/**
 * LDS findHotelDealsAdv treats city "ALL" / subject "ALL" as a real filter and
 * returns NO_RESULTS. Empty city (omit hotelCities) is the all-destinations contract.
 */
export function sanitizeLdsSearchRequestBody(data) {
  if (data == null || typeof data !== "object" || Array.isArray(data)) return data;
  const body = { ...data };
  const city = normalizeLdsCityCodeForPayload(body.city);
  if (city) body.city = city;
  else delete body.city;

  const destinationCode = normalizeLdsCityCodeForPayload(
    body.destinationCode || body.destination_code
  );
  if (destinationCode) {
    body.destinationCode = destinationCode;
    delete body.destination_code;
  } else {
    delete body.destinationCode;
    delete body.destination_code;
  }

  if (Array.isArray(body.categories)) {
    body.categories = body.categories.filter((value) => !isLdsAllSentinelCategory(value));
  }
  if (Array.isArray(body.cities)) {
    body.cities = body.cities.map(normalizeLdsCityCodeForPayload).filter(Boolean);
    if (body.cities.length === 0) delete body.cities;
  }
  return body;
}

function matchesLdsAutocompleteAllSentinel(primary) {
  if (!primary || typeof primary !== "object") return false;
  const sentinel =
    Array.isArray(LDS_AUTOCOMPLETE_MORE) && LDS_AUTOCOMPLETE_MORE.length > 0
      ? LDS_AUTOCOMPLETE_MORE[0]
      : null;
  if (!sentinel || typeof sentinel !== "object") return false;
  const cc = String(primary.CITYCODE ?? "").trim().toUpperCase();
  const ac = String(primary.AIRPORTCODE ?? "").trim().toUpperCase();
  const sc = String(sentinel.CITYCODE ?? "").trim().toUpperCase();
  const sac = String(sentinel.AIRPORTCODE ?? "").trim().toUpperCase();
  return Boolean(sc && cc === sc && ac === sac);
}

/**
 * Shared packages + organized-tours destination → API `city` field.
 * Uses AIRPORTCODE then CITYCODE (same as legacy buildLdsPayload); never sends literal "ALL".
 */
export function resolveLdsSearchCityFromLocation(loc) {
  const primary = getOrganizedTourPrimaryLocation(loc);
  if (!primary) {
    return { city: "", isAllDestinations: false, primary: null, rawCode: "" };
  }
  const cityCode = String(primary.CITYCODE ?? "").trim().toUpperCase();
  const airportCode = String(primary.AIRPORTCODE ?? "").trim().toUpperCase();
  if (
    cityCode === "ALL" ||
    airportCode === "ALL" ||
    matchesLdsAutocompleteAllSentinel(primary)
  ) {
    return { city: "", isAllDestinations: true, primary, rawCode: "ALL" };
  }
  const rawCode = primary.AIRPORTCODE || primary.CITYCODE || "";
  const city = normalizeLdsCityCodeForPayload(rawCode);
  return {
    city,
    isAllDestinations: false,
    primary,
    rawCode: String(rawCode || "").trim(),
  };
}

/** LDS_AUTOCOMPLETE_MORE[0]: "כל היעדים" — לחוזה עם city "". */
export function isOrganizedTourAllDestinationsLocation(loc) {
  return resolveLdsSearchCityFromLocation(loc).isAllDestinations;
}

/** Organized tours use month picker — not calendarDateArrival/Leave. */
export function isPackageMonthSearchReady(packageMonth, location) {
  if (!String(packageMonth || "").trim()) return false;
  const resolved = resolveLdsSearchCityFromLocation(location);
  if (resolved.isAllDestinations) return true;
  const primary = resolved.primary;
  return Boolean(
    primary &&
      (String(primary.CITYCODE || "").trim() ||
        String(primary.AIRPORTCODE || "").trim()),
  );
}

/** Unique dep/ret pairs whose departure falls in `packageMonth` (MM/YYYY). */
export function getPackageMonthDateCombinations(
  ldsDates,
  packageMonth,
  limit = 50,
) {
  const monthKey = String(packageMonth || "").trim();
  if (!monthKey || !ldsDates) return [];
  const arr = Array.isArray(ldsDates) ? ldsDates : [ldsDates];
  const seen = new Set();
  const out = [];
  for (const link of arr) {
    if (!link) continue;
    const dep = normalizeDateToDdMmYyyy(extractDepDate(link));
    const ret = normalizeDateToDdMmYyyy(extractRetDate(link));
    if (!dep || !ret) continue;
    const parts = dep.split("/");
    if (parts.length !== 3) continue;
    const [, mm, yyyy] = parts;
    if (`${mm}/${yyyy}` !== monthKey) continue;
    const comboKey = `${dep}|${ret}`;
    if (seen.has(comboKey)) continue;
    seen.add(comboKey);
    out.push({ depDate: dep, retDate: ret });
    if (out.length >= limit) break;
  }
  return out;
}

export function isOrganizedTourSearchReady(tourMonth, location) {
  if (!String(tourMonth || "").trim()) return false;
  if (resolveLdsSearchCityFromLocation(location).isAllDestinations) return true;
  const primary = getOrganizedTourPrimaryLocation(location);
  if (!primary) return false;
  const cityCode = String(primary.CITYCODE ?? "").trim();
  const airportCode = String(primary.AIRPORTCODE ?? "").trim();
  return Boolean(cityCode || airportCode);
}
